import {
  QrCodeIcon,
  ClipboardIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useMemo } from "react";
import { DetailField } from "@/core/components/detail-field";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { ReferralSourceReloadButton } from "@/business-settings/referral-sources/referral-source-sidebar/reload-button";
import { useBusiness } from "@/core/business";
import { downloadQRCode } from "@/core/utils/download-qr";
import { generateAdvocateSignupUrl } from "@/core/utils/url";
import { SIGNUP_SOURCE_CLASS_NAMES } from "@/referral-sources/constants";
import {
  SignupSource,
  ReferralSource,
} from "@/referral-sources/referral-source";

interface SignupSourceCodeInfoProps {
  businessId?: string;
  signupSource: SignupSource;
}

export const SignupSourceCodeInfo = ({
  businessId,
  signupSource,
}: SignupSourceCodeInfoProps) => {
  const { selectedBusiness } = useBusiness();
  const { signup_source_code, class_name, display_identification } =
    signupSource;
  const {
    id: signupSourceCodeId,
    counter,
    qr_code_url,
  } = signup_source_code || {};
  const isMarketingReferralSource =
    class_name === SIGNUP_SOURCE_CLASS_NAMES.marketingReferralSource;
  const referralCodeStr = `Referral${counter || ""}`;

  const advocateSignupUrl = useMemo(() => {
    if (!selectedBusiness?.phone_number || !counter) return null;
    return generateAdvocateSignupUrl(
      selectedBusiness.phone_number,
      String(counter),
    );
  }, [selectedBusiness?.phone_number, counter]);

  const handleCopyToClipboard = useCallback(() => {
    try {
      navigator.clipboard.writeText(referralCodeStr);
      toast.success("Referral code copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy referral code", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    }
  }, [referralCodeStr]);

  const handleCopyAdvocateSignupUrl = useCallback(() => {
    if (!advocateSignupUrl) return;
    try {
      navigator.clipboard.writeText(advocateSignupUrl);
      toast.success("Advocate signup link copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy advocate signup link", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    }
  }, [advocateSignupUrl]);

  const handleQRCodeDownload = useCallback(() => {
    if (qr_code_url && signupSourceCodeId) {
      const fileNameKey = [
        display_identification.replace(/[^a-z0-9]/gi, "_"),
        signupSourceCodeId.split("-").at(-1),
      ].join("_");

      downloadQRCode(qr_code_url, {
        fileName: `Volca_QR_Code_${fileNameKey}.png`,
        onSuccess: () => {
          toast.success("QR code downloaded successfully");
        },
        onError: (error) => {
          toast.error("Failed to download QR code", {
            description:
              error instanceof Error ? error.message : "Please try again",
          });
        },
      });
    }
  }, [qr_code_url, signupSourceCodeId, display_identification]);

  if (signup_source_code) {
    return (
      <Stack direction="row" gap="none" align="center" fullWidth>
        <DetailField
          label="Referral Code"
          message={referralCodeStr}
          extraInfo={
            <p>
              This referral code is unique to{" "}
              <b>{signupSource.display_identification}</b>. Anybody that texts
              in <b>{referralCodeStr}</b> will be a new advocate attributed to{" "}
              <b>{signupSource.display_identification}</b>.
            </p>
          }
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="icon"
              size="icon"
              type="button"
              onClick={handleCopyToClipboard}
            >
              <ClipboardIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Copy <b>{referralCodeStr}</b> to clipboard
          </TooltipContent>
        </Tooltip>
        {advocateSignupUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="icon"
                size="icon"
                type="button"
                onClick={handleCopyAdvocateSignupUrl}
              >
                <LinkIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <b>Copy advocate signup link</b> This link can be shared on social
              media, in posts, or sent online. It will redirect users to a page
              that helps them send the referral code <b>{referralCodeStr}</b>{" "}
              via SMS.
            </TooltipContent>
          </Tooltip>
        )}
        {qr_code_url && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="icon"
                size="icon"
                type="button"
                onClick={handleQRCodeDownload}
              >
                <QrCodeIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <b>Download automatic signup QR code</b> This QR code will
              auto-write a text to your business hotline with the referral code{" "}
              <b>{referralCodeStr}</b>. Have this person show the QR code to
              people they know to sign them up as advocates linked directly to
              them.
            </TooltipContent>
          </Tooltip>
        )}

        {!qr_code_url && isMarketingReferralSource && businessId && (
          <ReferralSourceReloadButton
            businessId={businessId}
            referralSource={signupSource as ReferralSource}
            entityName="QR Code"
          />
        )}
      </Stack>
    );
  } else if (isMarketingReferralSource && businessId) {
    return (
      <ReferralSourceReloadButton
        businessId={businessId}
        referralSource={signupSource as ReferralSource}
        entityName="Referral Code info"
      />
    );
  } else {
    return null;
  }
};
